import * as Sentry from "@sentry/react";
import { AuthError } from "@supabase/supabase-js";
import { isEmpty } from "lodash-es";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";

import { noop } from "common";
import { LoadingOverlay } from "components";
import { Maybe, Organization, Space, SupaUser } from "types";

import { useOrganizationsAndSpaces } from "./hooks/useOrganizationsAndSpaces";
import { LocateSupabaseClient } from "../../services";
import { deriveMfaState, initialMfaState, MfaState } from "../mfa";

const adminRoles = ["admin"];
const writeRoles = [...adminRoles, "writer"];

interface Permissions {
  canWrite: boolean;
  canAdmin: boolean;
  canInvite: boolean;
}

export interface AuthState {
  user: Maybe<SupaUser>;
  isSSOUser: boolean;
  mfa: MfaState;
  error: AuthError | null;
  supabase: LocateSupabaseClient;
  permissions: Permissions;
  currentOrganization: Maybe<Organization>;
  organizations: Organization[];
  currentSpaces: Space[];
  changeSpaces: (space: Space[]) => void;
  switchOrganization: (organization: Organization) => Promise<void> | void;
}

const initialState: AuthState = {
  user: null,
  isSSOUser: false,
  mfa: initialMfaState,
  error: null,
  organizations: [],
  currentOrganization: null,
  currentSpaces: [],
  supabase: {} as LocateSupabaseClient,
  permissions: {
    canWrite: false,
    canAdmin: false,
    canInvite: false
  },
  changeSpaces: noop,
  switchOrganization: noop
};

export const authInitialState = initialState;

export const AuthContext = createContext<AuthState>(initialState);

interface AuthProviderProps {
  supabaseClient: LocateSupabaseClient;
}

export const AuthProvider = ({
  children,
  supabaseClient
}: PropsWithChildren<AuthProviderProps>) => {
  const [{ user, error, mfa }, setAuthState] = useState<
    Pick<AuthState, "user" | "error" | "mfa">
  >({
    user: null,
    error: null,
    mfa: initialMfaState
  });

  // Until the MFA challenge is passed the session is aal1, which fails aal2-restricted
  // RLS policies — hold off on fetching organizations/spaces until then. A user who still
  // owes enrollment is aal1 as well, and an empty profiles result signs them straight out.
  const { isOrganizationsFetchInProgress, ...accessArea } = useOrganizationsAndSpaces(
    mfa.isChallengeRequired || mfa.isEnrollmentRequired ? null : user
  );

  const [isSessionCheckInProgress, setSessionCheckProgress] = useState(true);

  const getUser = useCallback(async () => {
    const { data, error } = await supabaseClient.auth.getSession();
    const currentUser = data.session?.user as SupaUser | undefined;

    if (error) {
      setAuthState({ user: null, error, mfa: initialMfaState });
    } else {
      setAuthState({ user: currentUser, error: null, mfa: deriveMfaState(data.session) });
    }

    setSessionCheckProgress(false);
  }, [supabaseClient.auth]);

  useEffect(() => {
    setSessionCheckProgress(true);

    getUser();

    const {
      data: { subscription }
    } = supabaseClient.auth.onAuthStateChange(async (_, session) => {
      const user = session?.user as SupaUser | undefined;

      if (user && isEmpty(user.app_metadata.profile)) {
        setAuthState({
          user: null,
          error: new AuthError("User profile is invalid."),
          mfa: initialMfaState
        });
        await supabaseClient.auth.signOut();

        return;
      } else if (user) {
        setAuthState({ user, error: null, mfa: deriveMfaState(session) });
      } else {
        setAuthState({ user: null, error: null, mfa: initialMfaState });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [getUser, supabaseClient.auth]);

  useEffect(() => {
    Sentry.setUser(user ? user : null);

    Sentry.setExtra("current_org", accessArea.currentOrganization);
    Sentry.setExtra("current_spaces", accessArea.currentSpaces);
  }, [accessArea.currentOrganization, accessArea.currentSpaces, user]);

  const isSSOUser = !!user?.app_metadata.provider?.startsWith("sso:");

  return (
    <AuthContext.Provider
      value={{
        user: user as SupaUser,
        isSSOUser,
        mfa,
        supabase: supabaseClient,
        permissions: {
          canWrite: writeRoles.includes(user?.app_metadata.profile.role ?? ""),
          canAdmin: adminRoles.includes(user?.app_metadata.profile.role ?? ""),
          canInvite: adminRoles.includes(user?.app_metadata.profile.role ?? "") && !isSSOUser
        },
        error,
        ...accessArea
      }}>
      {isSessionCheckInProgress ? <LoadingOverlay /> : children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }

  return context;
};
